/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { format, fromUnixTime } from "date-fns";
import { BsDatabaseFill, BsX, BsChatLeftText } from "react-icons/bs";
import {
  MdOutlinePhoneCallback,
  MdOutlinePhoneForwarded,
  MdBlock,
  MdOutlineWarningAmber,
  MdOutlineSell,
} from "react-icons/md";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, CircularProgress, Select, SelectItem } from "@nextui-org/react";
import { Tooltip } from "react-tooltip";
import { useNavigate, useParams } from "react-router-dom";
import { AiFillPauseCircle, AiFillPlayCircle } from "react-icons/ai";
import { IoCloseSharp } from "react-icons/io5";

import CallModal from "./CallModal";
import CallSearchBar from "./components/CallSearchBar";
import CustomCopyToClipboard from "../../components/Kit/CustomCopyToClipboard";
import Modal from "../../components/Modal";
import PaginationTable from "../../components/PaginationTable";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";
import LiveContentModalContent from "../live-content-search/LiveContentModalContent";
import AStarGradeDisplay from "../../components/Kit/AStarGradeDisplay";
import {
  CALL_DURATION_FILTER_TYPE,
  formatCallDurationRange,
  getCallDurationRange,
  withCallDurationRange,
} from "../../components/CallDurationDropdown";

import { useAppContext } from "../../contexts/AppContext";
import {
  addSelectedCall,
  addSelectedCalls,
  fetchDeepLinkCallInAlgolia,
  getCallsInAlgolia,
  removeSelectedCall,
  removeSelectedCalls,
} from "../../redux/actions";
import { getAlgoliaInfos } from "../../redux/actions/algoliaActions";
import { calls_table_columns } from "../../constants/tableHeaders";
import { handleSortingWithGrade } from "../../utils/sortUtils";
import getConfig from "../../conf/config";
import myAxios from "../../utils/axiosWrapper";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import { formatCallDuration } from "../../utils/callsUtils";
import MHoverToggleMenu from "../../components/Kit/MHoverToggleMenu";
import { auth } from "../../conf/firebase";
import useWindowSize from "../../hooks/useWindowSize";
import { transformFromUTC } from "../../utils/dateUtils";
import { GradeSliderDropdown } from "../../components/Navbar";

// Upsell page filter: params ["1"] = with upsell, ["0"] = without, no filter =
// any. A legacy `?filter=upsell` link (no params) means "with upsell", as the
// backend reads it (calls_router.get_upsell_filter_string).
const UPSELL_FILTER_TYPE = "upsell";
const UPSELL_ANY = "all";

const getUpsellParam = (pageFilters = []) => {
  const upsellFilter = pageFilters.find((item) => item?.type === UPSELL_FILTER_TYPE);
  if (!upsellFilter) return null;
  return upsellFilter?.params?.[0] === "0" ? "0" : "1";
};

const Calls = () => {
  const { t } = useTranslation();
  let { callId } = useParams();
  const { currentUser } = auth;

  const [width, _height] = useWindowSize();
  const isSmallScreen = width <= 1350;

  const filteredTableColumns = (
    isSmallScreen
      ? calls_table_columns.filter((column) => !column?.hideOnSmallScreens)
      : calls_table_columns
  ).map((column) => ({ ...column, label: column.label ? t(column.label) : column.label }));

  const { user } = useSelector((state) => state.user);
  const userPermissions = user?.permissions || [];
  const isSuperAdmin = userPermissions.includes("SUPER_ADMIN");

  const urlParams = new URLSearchParams(window.location.search);
  const filterQueryParameter = urlParams.get("filter");
  const searchQueryParameter = urlParams.get("search");
  // Comma-separated values for filters that take params (e.g. call_direction).
  const filterParamsQueryParameter = urlParams.get("params");

  const [gradeValue, setGradeValue] = useState([0, 10]);
  const [frustrationGradeValue, setFrustrationGradeValue] = useState([6, 10]);
  const [pageFilters, setPageFilters] = useState(
    filterQueryParameter
      ? [
          {
            type: filterQueryParameter,
            ...(filterParamsQueryParameter
              ? { params: filterParamsQueryParameter.split(",") }
              : {}),
          },
        ]
      : []
  );
  const [organisations, setOrganisations] = useState({});
  const [isOrganisationsLoading, setIsOrganisationsLoading] = useState(true);
  // All operators, deactivated ones included: selected ids and pills still need
  // their names. The filter menu only offers the active ones.
  const [operators, setOperators] = useState([]);
  const [isOperatorsLoading, setIsOperatorsLoading] = useState(true);
  const [nbCommentsByCall, setNbCommentsByCall] = useState({});
  const [isNbCommentsByCallLoading, setIsNbCommentsByCallLoading] = useState(false);
  const [hasStartedOrganisationsFetch, setHasStartedOrganisationsFetch] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);

  const {
    debouncedSearchText: searchText,
    setSearchText,
    callsDateRange,
    teamGrades,
    individualGrades,
    setPreventSearchReset,
    handleOnGradeChange,
  } = useAppContext();
  const {
    calls,
    loading,
    selectedCalls = [],
    deepLinkCall = {},
  } = useSelector((state) => state.calls.calls);
  const selectedcallsIds = selectedCalls?.map((call) => call.id) || [];
  const [currentCalls, setCurrentCalls] = useState([]);

  useEffect(() => {
    setCurrentCalls(calls);
  }, [calls]);

  const fetchNbCommentsByCall = async () => {
    setIsNbCommentsByCallLoading(true);
    const response = await myAxios.put(
      `${getConfig().API_URL}/audio/api/v1/comments/get_nb_calls_comments`,
      {
        call_ids: currentCalls.map((item) => item.id),
      }
    );
    const nbCommentsByCallsRetrieved = response?.data?.[0]?.comments || [];

    setNbCommentsByCall(nbCommentsByCallsRetrieved);
    setIsNbCommentsByCallLoading(false);
  };

  const fetchCompanyOrganisations = async () => {
    setIsOrganisationsLoading(true);
    try {
      const response = await myAxios.get(
        `${getConfig().API_URL}/audio/api/v1/admin/company_organisations`
      );
      const retrievedOrganisations = response?.data?.[0] || {};
      setOrganisations(retrievedOrganisations);
    } catch (error) {
      console.error("Error fetching company organisations:", error);
    } finally {
      setIsOrganisationsLoading(false);
    }
  };

  const fetchCompanyOperators = async () => {
    setIsOperatorsLoading(true);
    try {
      const response = await myAxios.get(
        `${getConfig().API_URL}/audio/api/v1/admin/simple_operators`
      );
      setOperators(response?.data?.[0]?.operators || []);
    } catch (error) {
      console.error("Error fetching company operators:", error);
    } finally {
      setIsOperatorsLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser || currentUser === null || hasStartedOrganisationsFetch || !isInitialized) {
      return;
    }
    setHasStartedOrganisationsFetch(true);
    fetchCompanyOrganisations();
    fetchCompanyOperators();
  }, [isInitialized, user, hasStartedOrganisationsFetch]);

  useEffect(() => {
    if (currentCalls.length <= 0) {
      return;
    }

    if (!currentUser || currentUser === null) {
      return;
    }

    fetchNbCommentsByCall();
  }, [currentCalls, user]);

  const { company_config, loading: algoliaLoading } = useSelector((state) => state.algolia.algolia);

  const display_tz = company_config?.display_tz;

  const [selectedItem, setSelectedItem] = useState(null);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);
  const [hasOpenedDeepLink, setHasOpenedDeepLink] = useState(false);
  const navigate = useNavigate();
  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "call_timestamp",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const {
    sidebarAudioUrl,
    setSidebarAudioUrl,
    setSidebarCallId,
    sidebarIsAudioPlaying,
    setSidebarIsAudioPlaying,
    setSidebarAudioTime,
    sidebarAudioRef,
    setSidebarAutoPlay,
    setPopupAudioUrl,
    setPopupCallId,
    setPopupAudioTime,
    setPopupIsAudioPlaying,
    popupIsAudioPlaying,
    popupAudioTime,
    popupAudioSpeed,
    setSidebarAudioSpeed,
  } = useAppContext();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAlgoliaInfos());
    if (searchQueryParameter) {
      setSearchText(searchQueryParameter);
    }
  }, []);

  const pageFilterToggled = pageFilters.map((item) => item.type);
  const callDurationRange = getCallDurationRange(pageFilters);

  const setCallDurationRange = (range) => {
    setPageFilters(withCallDurationRange(pageFilters, range));
  };

  const upsellParam = getUpsellParam(pageFilters);

  // value: "1" (with upsell), "0" (without) or null (any).
  const setUpsellFilter = (value) => {
    const otherFilters = pageFilters.filter((item) => item?.type !== UPSELL_FILTER_TYPE);
    setPageFilters(
      value ? [...otherFilters, { type: UPSELL_FILTER_TYPE, params: [value] }] : otherFilters
    );
  };

  const getUpsellLabel = (value) => (value === "0" ? t("Sans upsell") : t("Avec upsell"));

  const fetchCalls = () => {
    console.log("fetching calls with params: ", {
      individualGrades,
      callsDateRange,
      sortDescriptor,
      searchText,
      algoliaLoading,
      page,
      rowsPerPage,
      pageFilters,
      frustrationGradeValue,
    });
    const isLiveContentSearch = false;
    const isFrustratedToggled = pageFilterToggled.includes("frustrated");

    dispatch(
      getCallsInAlgolia(
        searchText,
        {
          start_date: callsDateRange.startDate,
          end_date: callsDateRange.endDate,
          ...teamGrades,
          ...individualGrades,
          min_frustration_grade: isFrustratedToggled ? frustrationGradeValue[0] : 0,
          max_frustration_grade: isFrustratedToggled ? frustrationGradeValue[1] : 10,
        },
        page - 1,
        rowsPerPage,
        sortDescriptor,
        isLiveContentSearch,
        pageFilters,
        display_tz
      )
    );
  };

  useEffect(() => {
    if (!dispatch || algoliaLoading || !user) {
      return;
    }
    setIsInitialized(true);
  }, [dispatch, algoliaLoading, user]);

  useEffect(() => {
    if (!dispatch || algoliaLoading) {
      return;
    }

    if (!isInitialized) {
      return;
    }

    fetchCalls();
  }, [
    isInitialized,
    individualGrades,
    callsDateRange,
    sortDescriptor,
    searchText,
    dispatch,
    algoliaLoading,
    page,
    rowsPerPage,
    pageFilters,
    frustrationGradeValue,
  ]);

  // The guard below is one-shot, so it has to be released whenever the URL
  // points at a different call - otherwise returning to a call from the sidebar
  // player silently does nothing while Calls is already mounted.
  useEffect(() => {
    setHasOpenedDeepLink(false);
  }, [callId]);

  const fetchDeepLinkCall = () => {
    if (!callId) {
      return;
    }

    dispatch(fetchDeepLinkCallInAlgolia(callId));
  };

  useEffect(() => {
    if (!callId) {
      return;
    }

    if (hasOpenedDeepLink) {
      return;
    }

    fetchDeepLinkCall();
  }, [callId, dispatch, algoliaLoading, hasOpenedDeepLink, user]);

  useEffect(() => {
    setPreventSearchReset(false);
  });

  const onOpenCall = (item) => {
    if (item && item?.analysis) {
      setSelectedItem(item);
      setPopupAudioUrl(item.audio_url);
      setPopupCallId(item.id);
      setPopupAudioTime(0);
      setSidebarAutoPlay(false);
      setIsCallModalOpen(true);
    }
  };

  useEffect(() => {
    if (!callId || !deepLinkCall?.id) {
      return;
    }

    if (callId !== deepLinkCall?.id) {
      return;
    }

    if (hasOpenedDeepLink) {
      return;
    }

    setHasOpenedDeepLink(true);
    onOpenCall(deepLinkCall);
  }, [deepLinkCall, callId, hasOpenedDeepLink]);

  const tableEmptyState = (
    <div className="w-full h-full flex-1 flex flex-col items-center justify-center">
      <BsDatabaseFill className="text-gray-300 w-12 h-12" />
      <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
    </div>
  );

  const onCloseCallModal = () => {
    setIsCallModalOpen(false);

    if (callId) {
      navigate("/calls", { replace: true });
    }

    if (popupIsAudioPlaying) {
      setPopupIsAudioPlaying(false);

      setSidebarIsAudioPlaying(true);
      setSidebarAudioTime(popupAudioTime);
      setSidebarAudioSpeed(popupAudioSpeed);
      if (sidebarAudioRef && sidebarAudioRef.current) {
        sidebarAudioRef.current.currentTime = popupAudioTime;
        sidebarAudioRef.current.playbackRate = popupAudioSpeed;
        sidebarAudioRef.current.play();
      }
    }
  };

  const onSearchNumber = (clientNumber) => {
    onCloseCallModal();
    setSearchText(clientNumber);
  };

  const onOpenFullTranscript = () => {
    setIsTranscriptModalOpen(true);
    // onCloseCallModal();
  };

  const onPlayPauseClick = (item) => (e) => {
    e.stopPropagation();

    if (!item || !item.audio_url) {
      return;
    }

    const isCurrentlyPlaying = item.audio_url === sidebarAudioUrl;
    if (!sidebarAudioRef.current) {
      return;
    }

    setSidebarAutoPlay(true);

    if (isCurrentlyPlaying) {
      if (sidebarIsAudioPlaying) {
        sidebarAudioRef.current.pause();
        setSidebarIsAudioPlaying(false);
      } else {
        sidebarAudioRef.current.play();
        setSidebarIsAudioPlaying(true);
      }
      return;
    }

    setSidebarAudioUrl(item.audio_url);
    setSidebarCallId(item.id);
    setSidebarAudioTime(0);
    sidebarAudioRef.current.currentTime = 0;
  };

  const onAddSelection = (item) => () => {
    dispatch(addSelectedCall(item));
  };

  const onRemoveSelection = (item) => () => {
    dispatch(removeSelectedCall(item));
  };

  const getIsSelectAllChecked = () => {
    if (currentCalls.length <= 0) {
      return false;
    }

    let hasNewCalls = false;
    let cpt = 0;

    while (!hasNewCalls && cpt < currentCalls.length) {
      const item = currentCalls[cpt];
      if (!selectedcallsIds.includes(item.id)) {
        hasNewCalls = true;
      }
      cpt++;
    }

    return !hasNewCalls;
  };

  const onSelectAll = () => {
    const isAllSelected = getIsSelectAllChecked();
    dispatch(isAllSelected ? removeSelectedCalls(currentCalls) : addSelectedCalls(currentCalls));
  };

  const renderCell = (item, columnKey) => {
    const isCurrentlyPlaying = item.audio_url === sidebarAudioUrl && sidebarIsAudioPlaying;
    const cellValue = item[columnKey];
    let { id: itemId } = item;
    let displayableId = itemId ? `...${itemId.substring(itemId.length - 6)}` : "";

    const isSelected = selectedcallsIds.includes(itemId);

    switch (columnKey) {
      case "checkbox":
        return (
          <Checkbox
            isSelected={isSelected}
            onValueChange={isSelected ? onRemoveSelection(item) : onAddSelection(item)}
          />
        );
      case "play_button":
        return (
          <div className="flex items-center space">
            <div
              className="w-auto h-auto flex items-center justify-center"
              onClick={onPlayPauseClick(item)}
              onKeyDown={onPlayPauseClick(item)}
            >
              {isCurrentlyPlaying && sidebarIsAudioPlaying ? (
                <AiFillPauseCircle
                  size={24}
                  color="#737373"
                  className="text-primary cursor-pointer"
                />
              ) : (
                <AiFillPlayCircle
                  size={24}
                  color="#737373"
                  className="text-primary cursor-pointer"
                />
              )}
            </div>
          </div>
        );
      case "call_id":
        return (
          <div className="flex items-center text-sm font-normal whitespace-normal">
            <CustomCopyToClipboard
              displayClassName="text-[#171717] min-w-[80px]"
              copyText={itemId}
              displayText={displayableId}
            />
            {isNbCommentsByCallLoading && (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
              />
            )}
            {!isNbCommentsByCallLoading && nbCommentsByCall[itemId] > 0 && (
              <>
                <Tooltip id={`tooltip-${itemId}`} />
                <div
                  className="ml-[12px]"
                  data-tooltip-id={`tooltip-${itemId}`}
                  data-tooltip-content={`${nbCommentsByCall[itemId]} comment${
                    nbCommentsByCall[itemId] > 1 ? "s" : ""
                  }`}
                >
                  <BsChatLeftText
                    size={16}
                    color="#737373"
                  />
                </div>
              </>
            )}
          </div>
        );
      case "operator_name":
        return (
          <ACircleUserDisplay
            firstname={item.operator_name
              .split(" ")
              .map((item) => capitalizeFirstLetter(item))
              .join(" ")}
            lastname=""
            email=""
            hideUserPreview={isSmallScreen}
          />
        );
      case "call_timestamp":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal flex flex-col space-y-1">
            <span>{format(transformFromUTC(item.call_timestamp, display_tz), "MMM dd, yyyy")}</span>
            <span className="text-gray-400 text-xs">
              {format(transformFromUTC(item.call_timestamp, display_tz), "HH:mm:ss")}
            </span>
          </div>
        );
      case "duration":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal tabular-nums">
            {formatCallDuration(item?.call_duration_seconds)}
          </div>
        );
      case "call_purpose":
        return (
          <div className="text-sm text-[#404040]/[0.6] font-normal">
            {item?.analysis?.call_purpose}
          </div>
        );
      case "grade":
        return (
          <div className="w-full flex justify-start items-center">
            <AStarGradeDisplay grade={item?.analysis?.call_overall_grade} />
          </div>
        );
      case "client_number":
        return (
          <div className="text-sm text-[#737373] font-normal flex items-center">
            <div>
              {item.incoming ? (
                <MdOutlinePhoneCallback
                  size={20}
                  color="#737373"
                  className="mr-2"
                />
              ) : (
                <MdOutlinePhoneForwarded
                  size={20}
                  color="#737373"
                  className="mr-2"
                />
              )}
            </div>
            {!item?.client_number || item.client_number === "None" ? (
              t("Unknown")
            ) : (
              <CustomCopyToClipboard
                wrapperClassName="!p-1 -ml-1"
                displayClassName="!pr-1.5"
                copyText={item.client_number}
                displayText={item.client_number}
              />
            )}
          </div>
        );
      default:
        return (
          <div className="text-sm text-[#404040]/[0.6] w-full float-left whitespace-normal">
            {cellValue}
          </div>
        );
    }
  };

  const handleOnRowAction = (item) => () => {
    onOpenCall(item);
  };

  const onResetPageFilters = () => {
    setPageFilters([]);
  };

  const onTogglePageFilter = (filter, valueToFilter, algoliaQuery, type) => {
    const currentlySelectedFilters = pageFilters.map((item) => item?.type);
    const isCurrentlySelected = currentlySelectedFilters.includes(filter);

    const isCustom = type === "custom";

    let newPageFilters = [];

    if (!isCurrentlySelected) {
      newPageFilters = [
        ...[...pageFilters].filter((item) => !item?.isCustom),
        { type: filter, valueToFilter, algoliaQuery, isCustom },
      ];
    } else {
      newPageFilters = [...pageFilters].filter((item) => item.type !== filter);
    }

    setPageFilters(newPageFilters);
  };

  const onSelectGradeFilter = (filterItem) => (gradeValues) => {
    const { type, label } = filterItem;
    if (!gradeValues || gradeValues.length <= 0) {
      onTogglePageFilter(filterItem.key);
      return;
    }
    // frustrated and handled separately
    let newPageFilters = [];
    const isCurrentlyToggled = pageFilterToggled.includes(filterItem.key);
    let algoliaQuery = "";

    if (type === "number") {
      algoliaQuery = `analysis.${filterItem?.analysis_key} >= ${gradeValues[0]} AND analysis.${filterItem?.analysis_key} <= ${gradeValues[1]}`;
    } else if (type === "category") {
      algoliaQuery = `(${gradeValues
        ?.map((value) => `analysis.${filterItem?.analysis_key}:"${value}"`)
        ?.join(" OR ")})`;
    } else if (type === "bool") {
      // For bool: gradeValues is an array of "1" or "0"
      algoliaQuery = `(${gradeValues
        ?.map((value) => `analysis.${filterItem?.analysis_key}:${value}`)
        ?.join(" OR ")})`;
    }

    if (!isCurrentlyToggled) {
      newPageFilters = [
        ...pageFilters,
        { type: filterItem.key, param: gradeValues, label, algoliaQuery, isCustom: true },
      ];
    } else {
      newPageFilters = [
        ...pageFilters.filter((item) => item.type !== filterItem.key),
        { type: filterItem.key, param: gradeValues, label, algoliaQuery, isCustom: true },
      ];
    }

    setPageFilters(newPageFilters);
  };

  const setChoiceFilterParam = (filterKey) => (param) => {
    const choicePageFilterIndex = pageFilters.findIndex((item) => item.type === filterKey);

    if (choicePageFilterIndex === -1) {
      const newPageFilters = [...pageFilters];
      newPageFilters.push({ type: filterKey, params: [param] });
      setPageFilters(newPageFilters);

      return;
    }
    const currentlySelectedParams = pageFilters[choicePageFilterIndex]?.params || [];
    const isParamCurrentlySelected = currentlySelectedParams.includes(param);

    let newParams = [...currentlySelectedParams];

    if (!isParamCurrentlySelected) {
      newParams = [...newParams, param];
    } else {
      newParams = [...currentlySelectedParams].filter((item) => item !== param);
    }

    const newPageFilters = [...pageFilters];

    newPageFilters[choicePageFilterIndex].params = newParams;
    setPageFilters(newPageFilters);
  };

  const renderDefaultPageFilter = () => {
    const filterToDescriptionMap = {
      green: "Green Calls",
      red: "Red Calls",
      orange: "Orange Calls",
      answerphone: "Answering Machine",
      frustrated: "Frustrated Clients",
    };
    const filtersFromWidgetsList = Object.keys(filterToDescriptionMap);
    const filterToLabelMap = {
      department: "Department",
      subject: "Subject",
      call_direction: "Call Direction",
      client_number: "Client number",
      operators: "Opérateur",
    };

    const filterParamFormat = (type, param) => {
      if (type === "department") {
        return organisations?.departments?.find((item) => item.id === param)?.name || "-";
      }
      if (type === "operators") {
        const operator = operators?.find((item) => item.id === param);
        return operator ? `${operator.first_name || ""} ${operator.last_name || ""}`.trim() : "-";
      }

      return param;
    };

    const incoming_call_purposes = company_config?.incoming_call_purposes || [];
    const outgoing_call_purposes = company_config?.outgoing_call_purposes || [];

    const call_purposes = [...new Set([...incoming_call_purposes, ...outgoing_call_purposes])];

    let quickFilters = [
      {
        label: "Frustrated Clients",
        key: "frustrated",
        icon: MdOutlineWarningAmber,
      },
      // only add if feature flag
      {
        label: "Missed opportunities",
        key: "missed-opportunities",
        icon: MdBlock,
      },
      {
        label: t("Upsell"),
        key: UPSELL_FILTER_TYPE,
        icon: MdOutlineSell,
        type: UPSELL_FILTER_TYPE,
      },
    ];

    if (company_config?.custom_quick_filters && company_config?.custom_quick_filters?.length > 0) {
      quickFilters = [];
      company_config?.custom_quick_filters.forEach((customQuickFilter) => {
        if (customQuickFilter?.active) {
          quickFilters.push({
            ...customQuickFilter,
            label: customQuickFilter?.label,
            key: customQuickFilter?.label.toLowerCase().replaceAll(" ", "-"),
            algoliaQuery: customQuickFilter?.query,
            // valueToFilter: customQuickFilter?.value,
            // icon: MdOutlineSell,
          });
        }
      });
    }

    const alwaysDisplayedFilters = quickFilters
      ?.filter((filter) => !["number", "category", "bool"].includes(filter?.type))
      ?.map((quickFilter) => quickFilter?.key);

    const additionalFiltersToDisplay = [];
    pageFilters.forEach((pageFilter) => {
      const { type: filterType } = pageFilter;

      if (filterType !== null && !alwaysDisplayedFilters.includes(filterType)) {
        additionalFiltersToDisplay.push(pageFilter);
      }
    });

    return (
      <div className="flex gap-[8px] justify-between items-start">
        <div className="flex items-center gap-[8px] flex-wrap">
          {/* Date filter */}
          <div className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary">
            <span className="whitespace-nowrap">
              {`${format(callsDateRange.startDate, "dd/MM/yyyy")} - ${format(
                callsDateRange.endDate,
                "dd/MM/yyyy"
              )}`}
            </span>
          </div>
          {/* grade filter */}
          {(gradeValue[0] !== 0 || gradeValue[1] !== 10 || individualGrades?.exclude_na) && (
            <div
              onClick={() => {
                setGradeValue([0, 10]);
                handleOnGradeChange("individual", {
                  min_individual_grade: 0,
                  max_individual_grade: 10,
                  exclude_na: false,
                });
              }}
              onKeyDown={() => {
                setGradeValue([0, 10]);
                handleOnGradeChange("individual", {
                  min_individual_grade: 0,
                  max_individual_grade: 10,
                  exclude_na: false,
                });
              }}
              className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
            >
              <span className="whitespace-nowrap">
                {`Grade : ${Math.round(gradeValue[0])} - ${Math.round(gradeValue[1])}${
                  individualGrades?.exclude_na ? " (no N.A)" : ""
                }`}
              </span>
              <IoCloseSharp size={18} />
            </div>
          )}
          {pageFilterToggled.includes("frustrated") &&
            (frustrationGradeValue[0] !== 0 || frustrationGradeValue[1] !== 10) && (
              <div
                onClick={() => {
                  setFrustrationGradeValue([0, 10]);
                }}
                onKeyDown={() => {
                  setFrustrationGradeValue([0, 10]);
                }}
                className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
              >
                <span className="whitespace-nowrap">
                  {`Frustration grade : ${Math.round(frustrationGradeValue[0])} - ${Math.round(
                    frustrationGradeValue[1]
                  )}`}
                </span>
                <IoCloseSharp size={18} />
              </div>
            )}
          {/* Multiple choices filters */}
          {additionalFiltersToDisplay.map((pageFilter) => {
            if (pageFilter.type === CALL_DURATION_FILTER_TYPE) {
              return (
                <div
                  key={pageFilter.type}
                  onClick={() => setCallDurationRange([null, null])}
                  onKeyDown={() => setCallDurationRange([null, null])}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {`${t("Call duration")} : ${formatCallDurationRange(callDurationRange)}`}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            }

            // Only reached when the company's custom quick filters replace the
            // upsell select (e.g. opened from a shared `?filter=upsell` link).
            if (pageFilter.type === UPSELL_FILTER_TYPE) {
              return (
                <div
                  key={pageFilter.type}
                  onClick={() => setUpsellFilter(null)}
                  onKeyDown={() => setUpsellFilter(null)}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {`${t("Upsell")} : ${getUpsellLabel(upsellParam)}`}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            }

            if (filtersFromWidgetsList.includes(pageFilter.type)) {
              return (
                <div
                  key={pageFilter.type}
                  onClick={() => onTogglePageFilter(pageFilter.type)}
                  onKeyDown={() => onTogglePageFilter(pageFilter.type)}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {t(filterToDescriptionMap[pageFilter.type])}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            }

            if (pageFilter.param && pageFilter.param.length > 0) {
              return (
                <div
                  key={pageFilter.type}
                  onClick={() => onTogglePageFilter(pageFilter.type)}
                  onKeyDown={() => onTogglePageFilter(pageFilter.type)}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {`${pageFilter?.label} : ${pageFilter?.param
                      ?.map((p) => (p === "1" ? "Yes" : p === "0" ? "No" : p))
                      ?.join(", ")}`}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            }
            return pageFilter?.params?.map((filterParam) => {
              return (
                <div
                  key={`${pageFilter.type}-${filterParam}`}
                  onClick={() => setChoiceFilterParam(pageFilter.type)(filterParam)}
                  onKeyDown={() => setChoiceFilterParam(pageFilter.type)(filterParam)}
                  className="cursor-pointer flex items-center gap-[8px] rounded-md px-[12px] py-[8px] text-sm bg-[#4318ff]/[0.18] text-primary"
                >
                  <span className="whitespace-nowrap">
                    {`${t(filterToLabelMap[pageFilter.type])} : ${filterParamFormat(
                      pageFilter.type,
                      filterParam
                    )}`}
                  </span>
                  <IoCloseSharp size={18} />
                </div>
              );
            });
          })}
        </div>
        {/* Toggle filters */}
        <div className="flex items-center whitespace-nowrap border-1 border-[#E5E5E5] bg-white overflow-hidden rounded-md shadow-drop-100 overflow-x-scroll">
          {/* Frustration additional filters */}
          {pageFilterToggled.includes("frustrated") && (
            <div>
              <GradeSliderDropdown
                label="Frustration grade"
                sliderLabel="Select grade"
                value={frustrationGradeValue}
                onChange={(gradeValues) => {
                  setFrustrationGradeValue(gradeValues);
                }}
                hideQuickActions
              />
            </div>
          )}
          {quickFilters
            ?.filter((filter) => filter?.type === "custom" || !filter?.type)
            ?.map((quickFilter, qfIndex) => {
              const isSelected = pageFilterToggled.includes(quickFilter?.key);
              const htmlKey = `f-${quickFilter.key}`;
              const filterData = isSelected
                ? pageFilters.find((item) => item.type === quickFilter?.key)
                : { params: [] };

              const valueToFilter = quickFilter?.valueToFilter;
              const isLast = qfIndex === quickFilters?.length - 1;

              return (
                <div
                  key={htmlKey}
                  className={`font-semibold cursor-pointer flex items-center px-[12px] py-[8px] ${
                    isSelected ? "bg-[#4318ff]/[0.18] text-primary" : "hover:bg-[#F5F5F5]"
                  } ${isLast ? "" : "border-r-1 border-r-[#E5E5E5]"}`}
                  onClick={() =>
                    onTogglePageFilter(
                      quickFilter.key,
                      quickFilter?.valueToFilter,
                      quickFilter?.algoliaQuery,
                      quickFilter?.type
                    )
                  }
                  onKeyDown={() =>
                    onTogglePageFilter(
                      quickFilter.key,
                      quickFilter?.valueToFilter,
                      quickFilter?.algoliaQuery,
                      quickFilter?.type
                    )
                  }
                >
                  {quickFilter.icon && <quickFilter.icon size={18} />}
                  <span className="ml-[4px] text-sm">{quickFilter.label}</span>
                </div>
              );
            })}
          {quickFilters
            ?.filter((filter) => filter?.type === UPSELL_FILTER_TYPE)
            ?.map((quickFilter) => {
              const isLast =
                quickFilters.filter((f) => ["bool", "category", "number"].includes(f?.type))
                  .length === 0;

              return (
                <div
                  key={`f-${quickFilter.key}`}
                  className={`${isLast ? "" : "border-r-1 border-r-[#E5E5E5]"} ${
                    upsellParam ? "bg-[#4318ff]/[0.18]" : ""
                  }`}
                >
                  <Select
                    label={quickFilter.label}
                    aria-label={quickFilter.label}
                    className="h-[36px] w-[150px]"
                    classNames={{
                      trigger: `h-[36px] min-h-[36px] ${upsellParam ? "bg-transparent" : ""}`,
                      value: "text-sm",
                      label: "text-sm",
                    }}
                    selectionMode="single"
                    disallowEmptySelection
                    selectedKeys={new Set([upsellParam || UPSELL_ANY])}
                    onSelectionChange={(keys) => {
                      const [value] = Array.from(keys);
                      setUpsellFilter(value && value !== UPSELL_ANY ? value : null);
                    }}
                  >
                    <SelectItem
                      key={UPSELL_ANY}
                      value={UPSELL_ANY}
                    >
                      {t("Tous les appels")}
                    </SelectItem>
                    <SelectItem
                      key="1"
                      value="1"
                    >
                      {t("Avec upsell")}
                    </SelectItem>
                    <SelectItem
                      key="0"
                      value="0"
                    >
                      {t("Sans upsell")}
                    </SelectItem>
                  </Select>
                </div>
              );
            })}
          {quickFilters
            ?.filter((filter) => filter?.type === "bool")
            ?.map((quickFilter, qfIndex) => {
              const htmlKey = `f-${quickFilter.key}`;
              const toggledData = pageFilters.find((item) => item.type === quickFilter?.key);
              const isSelected = pageFilterToggled.includes(quickFilter?.key);

              const boolFilters = quickFilters.filter((f) => f.type === "bool");
              const isLast =
                qfIndex === boolFilters.length - 1 &&
                quickFilters.filter((f) => f.type === "category").length === 0 &&
                quickFilters.filter((f) => f.type === "number").length === 0;

              return (
                <div
                  key={htmlKey}
                  className={`${isLast ? "" : "border-r-1 border-r-[#E5E5E5]"}`}
                >
                  <Select
                    label={quickFilter.label}
                    className="h-[36px] w-[140px]"
                    classNames={{
                      trigger: "h-[36px] min-h-[36px]",
                      value: "text-sm",
                      label: "text-sm",
                    }}
                    selectionMode="multiple"
                    selectedKeys={new Set(toggledData?.param || [])}
                    onSelectionChange={(keys) => {
                      console.log({ keys });
                      const selectedArray = Array.from(keys);
                      onSelectGradeFilter(quickFilter)(selectedArray);
                    }}
                    renderValue={(items) => {
                      if (items.length === 0) {
                        return <span className="text-gray-400 text-sm">{quickFilter.label}</span>;
                      }
                      return (
                        <span className="text-sm">
                          {items?.map((item) => item.textValue).join(", ")}
                        </span>
                      );
                    }}
                  >
                    <SelectItem
                      key="1"
                      value="1"
                    >
                      Yes
                    </SelectItem>
                    <SelectItem
                      key="0"
                      value="0"
                    >
                      No
                    </SelectItem>
                  </Select>
                </div>
              );
            })}
          {quickFilters
            ?.filter((filter) => filter?.type === "category")
            ?.map((quickFilter, qfIndex) => {
              const htmlKey = `f-${quickFilter.key}`;
              const toggledData = pageFilters.find((item) => item.type === quickFilter?.key);
              const isSelected = pageFilterToggled.includes(quickFilter?.key);

              const categoryFilters = quickFilters.filter((f) => f.type === "category");
              const isLast =
                qfIndex === categoryFilters.length - 1 &&
                quickFilters.filter((f) => f.type === "number").length === 0;

              return (
                <div
                  key={htmlKey}
                  className={`${isLast ? "" : "border-r-1 border-r-[#E5E5E5]"}`}
                >
                  <Select
                    label={quickFilter.label}
                    className="h-[36px] w-[160px]"
                    classNames={{
                      trigger: "h-[36px] min-h-[36px]",
                      value: "text-sm",
                      label: "text-sm",
                    }}
                    selectionMode="multiple"
                    selectedKeys={new Set(toggledData?.param || [])}
                    onSelectionChange={(keys) => {
                      console.log({ keys });
                      const selectedArray = Array.from(keys);
                      onSelectGradeFilter(quickFilter)(selectedArray);
                    }}
                    renderValue={(items) => {
                      if (items.length === 0) {
                        return <span className="text-gray-400 text-sm">{quickFilter.label}</span>;
                      }
                      return (
                        <span className="text-sm">
                          {items?.map((item) => item.textValue).join(", ")}
                        </span>
                      );
                    }}
                  >
                    {quickFilter.categories?.map((category) => (
                      <SelectItem
                        key={category}
                        value={category}
                      >
                        {category}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              );
            })}
          {quickFilters
            ?.filter((filter) => filter?.type === "number")
            ?.map((quickFilter, qfIndex) => {
              const defaultGradeValue = [0, 10];
              const toggledData = pageFilters.find((item) => item.type === quickFilter?.key);
              const isSelected = pageFilterToggled.includes(quickFilter?.key);
              const selectedValue = isSelected
                ? toggledData?.param || defaultGradeValue
                : defaultGradeValue;

              const numberFilters = quickFilters.filter((f) => f.type === "number");
              const isLast = qfIndex === numberFilters.length - 1;

              return (
                <div
                  key={`f-${quickFilter.key}`}
                  className={`${isLast ? "" : "border-r-1 border-r-[#E5E5E5]"}`}
                >
                  <GradeSliderDropdown
                    label={quickFilter.label}
                    value={selectedValue}
                    onChange={(gradeValues) => {
                      onSelectGradeFilter(quickFilter)(gradeValues);
                    }}
                    hideQuickActions
                    hideStar
                  />
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 pt-0 pb-32 max-w-full">
      <CallSearchBar
        onTogglePageFilter={onTogglePageFilter}
        setChoiceFilterParam={setChoiceFilterParam}
        pageFilters={pageFilters}
        organisations={organisations}
        operators={operators}
        isOperatorsLoading={isOperatorsLoading}
        company_config={company_config}
        gradeValue={gradeValue}
        teamGrades={teamGrades}
        individualGrades={individualGrades}
        display_tz={display_tz}
        sortDescriptor={sortDescriptor}
        setGradeValue={setGradeValue}
        frustrationGradeValue={frustrationGradeValue}
        searchText={searchText}
        isOrganisationsLoading={isOrganisationsLoading}
        callDurationRange={callDurationRange}
        setCallDurationRange={setCallDurationRange}
      />
      <div className="mt-[14px]">{renderDefaultPageFilter()}</div>
      <PaginationTable
        items={currentCalls}
        selectAllAction={onSelectAll}
        isSelectAllChecked={getIsSelectAllChecked()}
        columns={filteredTableColumns}
        emptyState={tableEmptyState}
        isLoading={loading}
        renderCell={renderCell}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        onRowAction={handleOnRowAction}
        handleOnSort={handleSortingWithGrade}
        algoliaPaginationEnabled
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        // customFiltersRender={renderDefaultPageFilter()}
      />
      {isCallModalOpen && (
        <Modal
          isOpen={isCallModalOpen}
          showFooter={false}
          size="full"
          handleOnClose={onCloseCallModal}
          displayCloseButton={false}
        >
          <CallModal
            item={selectedItem}
            handleOnClose={onCloseCallModal}
            onOpenFullTranscript={onOpenFullTranscript}
            onSearchNumber={onSearchNumber}
            display_tz={display_tz}
          />
        </Modal>
      )}
      {isTranscriptModalOpen && (
        <Modal
          isOpen={isTranscriptModalOpen}
          showFooter={false}
          size="4xl"
          handleOnClose={() => setIsTranscriptModalOpen(false)}
        >
          <LiveContentModalContent
            item={selectedItem}
            disablePlayer
            handleOnClose={() => setIsTranscriptModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default Calls;
